import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Nav } from "../components/Nav";
import { useLang } from "../i18n";
import { AREA_META } from "../constants/areaMeta";
import { api } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import { InfoHint } from "../components/InfoHint";
import { VoiceButton } from "../components/VoiceButton";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Sparkles } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const emptySession = () => ({ type: "", duration_min: "", time_of_day: "" });
const last8Days = () =>
  Array.from({ length: 8 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - i);
    return d.toISOString().slice(0, 10);
  });

const initial = {
  nutrition: { meals: "", water_liters: "", meal_times: "", quality: 3 },
  exercise: { sessions: [emptySession()] },
  sleep: { hours: "", bedtime: "", waketime: "", quality: 3 },
  cold: { type: "", duration_min: "" },
  breathing: { minutes: "", technique: "" },
  relationships: { notes: "", quality: 3 },
};

export default function TrackDay() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const focusPillar = params.get("pillar");
  const [highlight, setHighlight] = useState(focusPillar || null);
  const [data, setData] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState(today());
  const days = last8Days();

  useEffect(() => {
    if (!focusPillar) return;
    const tmo = setTimeout(() => {
      const el = document.querySelector(`[data-testid="track-card-${focusPillar}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
    const clr = setTimeout(() => setHighlight(null), 2800);
    return () => { clearTimeout(tmo); clearTimeout(clr); };
  }, [focusPillar]);

  useEffect(() => {
    api.get(`/logs/${date}`).then((r) => {
      if (r.data && Object.keys(r.data).length) {
        const ex = r.data.exercise || {};
        const rawSessions = Array.isArray(ex.sessions) && ex.sessions.length
          ? ex.sessions
          : (ex.type || ex.duration_min || ex.time_of_day)
            ? [{ type: ex.type, duration_min: ex.duration_min, time_of_day: ex.time_of_day }]
            : [emptySession()];
        const sessions = rawSessions.map((s) => ({
          type: s.type || "",
          duration_min: s.duration_min || "",
          time_of_day: s.time_of_day || "",
          ...(s.from_diary ? { from_diary: true } : {}),
        }));
        setData({
          nutrition: { ...initial.nutrition, ...r.data.nutrition },
          exercise: { sessions },
          sleep: { ...initial.sleep, ...r.data.sleep },
          cold: { ...initial.cold, ...r.data.cold },
          breathing: { ...initial.breathing, ...r.data.breathing },
          relationships: { ...initial.relationships, ...r.data.relationships },
        });
      } else {
        setData(initial);
      }
    });
  }, [date]);

  const upd = (area, field, val) => setData((d) => ({ ...d, [area]: { ...d[area], [field]: val } }));
  const appendField = (area, field, txt) =>
    setData((d) => {
      const cur = d[area][field] || "";
      return { ...d, [area]: { ...d[area], [field]: cur ? cur + " " + txt : txt } };
    });

  const updSession = (i, field, val) =>
    setData((d) => ({
      ...d,
      exercise: { sessions: d.exercise.sessions.map((s, idx) => (idx === i ? { ...s, [field]: val } : s)) },
    }));
  const addSession = () =>
    setData((d) => ({ ...d, exercise: { sessions: [...d.exercise.sessions, emptySession()] } }));
  const removeSession = (i) =>
    setData((d) => ({ ...d, exercise: { sessions: d.exercise.sessions.filter((_, idx) => idx !== i) } }));

  const save = async () => {
    setBusy(true);
    try {
      const clean = {
        ...data,
        exercise: { sessions: data.exercise.sessions.filter((s) => s.type || s.duration_min || s.time_of_day) },
      };
      await api.post("/logs", { date, ...clean });
      toast.success(t("saved"));
      navigate("/dashboard");
    } catch (e) {
      toast.error("Errore");
    } finally {
      setBusy(false);
    }
  };

  const L = (it, en) => (lang === "it" ? it : en);
  const fromDiary =
    (data.nutrition && data.nutrition.from_diary) ||
    (data.exercise.sessions || []).some((s) => s.from_diary);

  const INFO = {
    nutrition: L("Descrivi il pasto in modo completo, inclusi i condimenti. Es: 'pasta con zucchine condita con burro e olio EVO'. Indica anche gli orari dei pasti.", "Describe the meal fully, including dressings/condiments. E.g. 'pasta with zucchini dressed with butter and EVO oil'. Add meal times too."),
    exercise: L("Specifica tipo, intensità e durata. Es: '1 ora di cardio ad alta intensità con pesi'. Aggiungi l'orario di ogni attività.", "Specify type, intensity and duration. E.g. '1 hour of high-intensity cardio with weights'. Add the time of each activity."),
    sleep: L("Indica ore dormite, orario in cui sei andato a letto e sveglia, e la qualità percepita.", "Enter hours slept, bedtime and wake time, and perceived quality."),
    cold: L("Indica il tipo (doccia fredda, bagno di ghiaccio...) e la durata in minuti.", "Enter the type (cold shower, ice bath...) and duration in minutes."),
    breathing: L("Indica i minuti e la tecnica usata (es. 4-7-8, box breathing).", "Enter the minutes and technique used (e.g. 4-7-8, box breathing)."),
    relationships: L("Annota con chi hai passato del tempo e com'è andata; valuta la qualità delle relazioni oggi.", "Note who you spent time with and how it went; rate today's relationships quality."),
  };

  return (
    <div className="App min-h-screen">
      <Nav />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-[#6E6B65] mb-4 hover:text-[#3F6E85]" data-testid="track-back">
          <ArrowLeft className="w-4 h-4" /> {t("back")}
        </button>
        <h1 className="font-serif text-4xl tracking-tight mb-1">{t("track_today")}</h1>
        <label className="flex items-center gap-2 text-sm text-[#6E6B65] mb-3">
          {t("track_date")}:
          <select
            data-testid="track-date-select"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="rounded-lg border border-stone-200 px-2 py-1 text-sm bg-white"
          >
            {days.map((d, i) => (
              <option key={d} value={d}>{i === 0 ? `${t("today_label")} (${d})` : d}</option>
            ))}
          </select>
        </label>
        <p className="text-sm text-[#6E6B65] mb-6">
          {L("Compila solo ciò che hai fatto: se non scrivi nulla in un'area, verrà considerata non svolta. Puoi registrare o modificare fino a 7 giorni indietro.",
             "Fill in only what you did: if you leave an area empty, it counts as not done. You can log or edit up to 7 days back.")}
        </p>

        {fromDiary && (
          <div className="rounded-xl bg-[#FBEDE7] border border-[#f2d6cb] text-[#B7410E] text-sm px-4 py-3 mb-6" data-testid="from-diary-note">
            {t("from_diary_note")}
          </div>
        )}

        <div className="space-y-6">
          <AreaCard area="nutrition" info={INFO.nutrition} highlight={highlight === "nutrition"}>
            {data.nutrition.from_diary && <DiaryTag />}
            <Field label={L("Cosa hai mangiato?", "What did you eat?")}>
              <div className="flex items-start gap-2">
                <Textarea data-testid="nutrition-meals" value={data.nutrition.meals} onChange={(e) => upd("nutrition", "meals", e.target.value)} className="rounded-lg flex-1" />
                <VoiceButton compact onResult={(txt) => appendField("nutrition", "meals", txt)} />
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label={L("Acqua (litri)", "Water (liters)")}>
                <Input data-testid="nutrition-water" type="number" step="0.1" value={data.nutrition.water_liters} onChange={(e) => upd("nutrition", "water_liters", e.target.value)} className="rounded-lg" />
              </Field>
              <Field label={L("Orari pasti", "Meal times")}>
                <Input data-testid="nutrition-times" value={data.nutrition.meal_times} onChange={(e) => upd("nutrition", "meal_times", e.target.value)} placeholder="8:00, 13:00, 20:00" className="rounded-lg" />
              </Field>
            </div>
            <RateField label={L("Qualità", "Quality")} value={data.nutrition.quality} onChange={(v) => upd("nutrition", "quality", v)} testid="nutrition-quality" />
          </AreaCard>

          <AreaCard area="exercise" info={INFO.exercise} highlight={highlight === "exercise"}>
            <div className="space-y-4">
              {data.exercise.sessions.map((s, i) => (
                <div key={i} className="rounded-xl border border-stone-200 p-4 bg-[#FBF9F4]" data-testid={`exercise-session-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs uppercase tracking-wider text-[#6E6B65] flex items-center gap-2">
                      {L("Attività", "Activity")} {i + 1}
                      {s.from_diary && <DiaryTag />}
                    </span>
                    {data.exercise.sessions.length > 1 && (
                      <button onClick={() => removeSession(i)} data-testid={`exercise-remove-${i}`} className="text-[#B7410E] hover:opacity-70" aria-label="remove">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <Field label={L("Sport / Tipo", "Sport / Type")}>
                      <div className="flex items-center gap-2">
                        <Input data-testid={`exercise-type-${i}`} value={s.type} onChange={(e) => updSession(i, "type", e.target.value)} className="rounded-lg flex-1" />
                        <VoiceButton compact onResult={(txt) => updSession(i, "type", s.type ? s.type + " " + txt : txt)} />
                      </div>
                    </Field>
                    <Field label={L("Minuti", "Minutes")}>
                      <Input data-testid={`exercise-duration-${i}`} type="number" value={s.duration_min} onChange={(e) => updSession(i, "duration_min", e.target.value)} className="rounded-lg" />
                    </Field>
                    <Field label={L("Orario", "Time")}>
                      <Input data-testid={`exercise-time-${i}`} value={s.time_of_day} onChange={(e) => updSession(i, "time_of_day", e.target.value)} placeholder="07:00" className="rounded-lg" />
                    </Field>
                  </div>
                </div>
              ))}
              <Button variant="outline" onClick={addSession} data-testid="exercise-add" className="rounded-full gap-2 border-dashed border-[#3F6E85]/40 text-[#3F6E85]">
                <Plus className="w-4 h-4" /> {t("add_activity")}
              </Button>
            </div>
          </AreaCard>

          <AreaCard area="sleep" info={INFO.sleep} highlight={highlight === "sleep"}>
            <div className="grid grid-cols-3 gap-4">
              <Field label={L("Ore dormite", "Hours slept")}>
                <Input data-testid="sleep-hours" type="number" step="0.5" value={data.sleep.hours} onChange={(e) => upd("sleep", "hours", e.target.value)} className="rounded-lg" />
              </Field>
              <Field label={L("A letto", "Bedtime")}>
                <Input data-testid="sleep-bedtime" value={data.sleep.bedtime} onChange={(e) => upd("sleep", "bedtime", e.target.value)} placeholder="23:00" className="rounded-lg" />
              </Field>
              <Field label={L("Sveglia", "Wake")}>
                <Input data-testid="sleep-waketime" value={data.sleep.waketime} onChange={(e) => upd("sleep", "waketime", e.target.value)} placeholder="07:00" className="rounded-lg" />
              </Field>
            </div>
            <RateField label={L("Qualità del sonno", "Sleep quality")} value={data.sleep.quality} onChange={(v) => upd("sleep", "quality", v)} testid="sleep-quality" />
          </AreaCard>

          <AreaCard area="cold" info={INFO.cold} highlight={highlight === "cold"}>
            <div className="grid grid-cols-2 gap-4">
              <Field label={L("Tipo (doccia, ghiaccio...)", "Type (shower, ice...)")}>
                <div className="flex items-center gap-2">
                  <Input data-testid="cold-type" value={data.cold.type} onChange={(e) => upd("cold", "type", e.target.value)} className="rounded-lg flex-1" />
                  <VoiceButton compact onResult={(txt) => appendField("cold", "type", txt)} />
                </div>
              </Field>
              <Field label={L("Durata (min)", "Duration (min)")}>
                <Input data-testid="cold-duration" type="number" value={data.cold.duration_min} onChange={(e) => upd("cold", "duration_min", e.target.value)} className="rounded-lg" />
              </Field>
            </div>
          </AreaCard>

          <AreaCard area="breathing" info={INFO.breathing} highlight={highlight === "breathing"}>
            <div className="grid grid-cols-2 gap-4">
              <Field label={L("Minuti", "Minutes")}>
                <Input data-testid="breathing-minutes" type="number" value={data.breathing.minutes} onChange={(e) => upd("breathing", "minutes", e.target.value)} className="rounded-lg" />
              </Field>
              <Field label={L("Tecnica", "Technique")}>
                <div className="flex items-center gap-2">
                  <Input data-testid="breathing-technique" value={data.breathing.technique} onChange={(e) => upd("breathing", "technique", e.target.value)} placeholder="4-7-8, box..." className="rounded-lg flex-1" />
                  <VoiceButton compact onResult={(txt) => appendField("breathing", "technique", txt)} />
                </div>
              </Field>
            </div>
          </AreaCard>

          <AreaCard area="relationships" info={INFO.relationships} highlight={highlight === "relationships"}>
            <Field label={L("Note (con chi, come è andata)", "Notes (with whom, how it went)")}>
              <div className="flex items-start gap-2">
                <Textarea data-testid="relationships-notes" value={data.relationships.notes} onChange={(e) => upd("relationships", "notes", e.target.value)} className="rounded-lg flex-1" />
                <VoiceButton compact onResult={(txt) => appendField("relationships", "notes", txt)} />
              </div>
            </Field>
            <RateField label={L("Come stai con le persone?", "How are you with people?")} value={data.relationships.quality} onChange={(v) => upd("relationships", "quality", v)} testid="relationships-quality" />
          </AreaCard>
        </div>

        <Button data-testid="save-day-btn" onClick={save} disabled={busy} className="mt-8 w-full rounded-full bg-[#3F6E85] hover:bg-[#2B4A59] h-12 text-base">
          {busy ? "..." : t("save_day")}
        </Button>
      </div>
    </div>
  );
}

const AreaCard = ({ area, info, highlight, children }) => {
  const { t } = useLang();
  const Icon = AREA_META[area].icon;
  return (
    <div className={`rounded-2xl p-6 bg-white border transition-all duration-500 scroll-mt-24 ${highlight ? "border-[#3F6E85] ring-4 ring-[#3F6E85]/20" : "border-stone-200"}`} data-testid={`track-card-${area}`}>
      <div className="flex items-center gap-3 mb-5">
        <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: AREA_META[area].tint }}>
          <Icon className="w-5 h-5" style={{ color: AREA_META[area].color }} />
        </span>
        <h3 className="font-serif text-2xl tracking-tight">{t(area)}</h3>
        {info && <span className="ml-auto"><InfoHint text={info} testid={`info-${area}`} /></span>}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm text-[#6E6B65]">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

const DiaryTag = () => {
  const { t } = useLang();
  return (
    <span data-testid="from-diary-tag" className="inline-flex items-center gap-1 rounded-full bg-[#FBEDE7] text-[#B5651D] text-xs font-semibold px-2.5 py-0.5 border border-[#f2d6cb]">
      <Sparkles className="w-3 h-3" /> {t("from_diary_tag")}
    </span>
  );
};

const RateField = ({ label, value, onChange, testid }) => (
  <div>
    <div className="flex justify-between mb-2">
      <Label className="text-sm text-[#6E6B65]">{label}</Label>
      <span className="text-sm font-semibold text-[#3F6E85]">{value}/5</span>
    </div>
    <Slider data-testid={testid} min={1} max={5} step={1} value={[value]} onValueChange={(v) => onChange(v[0])} />
  </div>
);
